import {
  type Country,
  isPossiblePhoneNumber,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input/max'

import {
  DEFAULT_PHONE_COUNTRY,
  formatPhoneNumber,
  isPhoneNumberEntered,
} from './phoneNumberHelpers'

// The geo lookup stores a lowercase ISO 3166 code ('us'); the phone input wants 'US'.
export function getPhoneCountry(countryCode?: string): Country {
  const code = countryCode?.toUpperCase()
  return code && isSupportedCountry(code) ? code : DEFAULT_PHONE_COUNTRY
}

export function isPhoneNumberValidForSubmission(
  phoneNumber: string,
  isRequired?: boolean,
): boolean {
  if (isRequired) {
    return isPhoneNumberValid(phoneNumber)
  }

  return !isPhoneNumberEntered(phoneNumber) || isPhoneNumberValid(phoneNumber)
}

// Same check as the server: libphonenumber IsValidNumber on Parse(value, "US").
export function isPhoneNumberValid(rawPhoneNumber: string): boolean {
  if (!isPhoneNumberEntered(rawPhoneNumber)) {
    return false
  }

  const phoneNumber = parsePhoneNumber(
    formatPhoneNumber(rawPhoneNumber),
    DEFAULT_PHONE_COUNTRY,
  )
  return !!phoneNumber && !phoneNumber.ext && phoneNumber.isValid()
}

// Numbers saved before the E.164 field (no '+', separators) are read like the server reads them,
// so the field shows the right country and the first edit does not reinterpret the digits.
export function toE164PhoneNumber(value: string): string {
  if (!value || /^\+\d+$/.test(value)) {
    return value
  }

  return (
    parsePhoneNumber(formatPhoneNumber(value), DEFAULT_PHONE_COUNTRY)?.number ??
    value
  )
}

const DIGITS = [...'0123456789']
const MAX_E164_DIGITS = 15

// Valid lengths are not contiguous (+13105550 is a valid 7-digit Canadian number, +13105550199
// a valid US one), so ask whether ANY longer number could pass, not just one more digit.
function canGrowInto(
  value: string,
  test: (phoneNumber: string) => boolean,
  minExtraDigits: number,
): boolean {
  if (minExtraDigits === 0 && test(value)) {
    return true
  }

  const digitCount = value.replace(/\D/g, '').length
  for (let extra = 1; digitCount + extra <= MAX_E164_DIGITS; extra++) {
    if (DIGITS.some(digit => test(value + digit.repeat(extra)))) {
      return true
    }
  }
  return false
}

// Whether typing `digit` at the end of the E.164 `value` can still lead to a valid number: refuse
// digits past the country's longest number, and past a valid number that cannot get longer.
export function canAppendPhoneDigit(value: string, digit: string): boolean {
  const next = value + digit
  if (
    canGrowInto(value, isPossiblePhoneNumber, 0) &&
    !canGrowInto(next, isPossiblePhoneNumber, 0)
  ) {
    return false
  }
  return !isValidPhoneNumber(value) || canGrowInto(value, isValidPhoneNumber, 1)
}
